import { useEffect, useRef, useState, useCallback } from 'react';
import { useInterval } from 'usehooks-ts';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useMapEvent } from 'features/mapping/hooks/useMapEvent';
import type { TimeFrame } from 'components/AnimatedTimelineControl';
import { usePlaybackState } from 'hooks/usePlaybackState';
import type {
  TerrierCadence,
  TerrierEnableForRange,
  TerrierLayer,
  TerrierOverlay,
  TerrierSourceConfig,
} from '../vendor/terrier';
import { useRadarAnimation } from './useRadarAnimation';
import { setupTerrierLayer, removeRadarMaskLayer } from './setupTerrierLayer';
import terrier from '../vendor/terrier';

interface TerrierSourceParams {
  config: TerrierSourceConfig;
  cadence: TerrierCadence;
  enableForRange: TerrierEnableForRange;
}

export interface TerrierRadarConfig {
  opacity: number;
  animationPeriod: number;
  refreshIntervalMs: number;
  sources: TerrierSourceParams[];
  maskOpacity?: number; // 0-1, dims base map below radar
}

export interface UseTerrierRadarOptions {
  config: TerrierRadarConfig;
  map: MapboxMap | null;
  visible: boolean;
  belowLayer?: string;
  onError?: (error: Error) => void;
}

export interface UseTerrierRadarResult {
  frames: TimeFrame[];
  currentFrameIndex: number;
  isPlaying: boolean;
  isLoading: boolean;
  onFrameChange: (index: number) => void;
  onPlayPause: () => void;
}

/**
 * Hook for managing TerrierWeb radar layer with animation controls.
 */
export const useTerrierRadar = ({
  config,
  map,
  visible,
  belowLayer,
  onError,
}: UseTerrierRadarOptions): UseTerrierRadarResult => {
  // Refs for Terrier instances
  const overlayRef = useRef<TerrierOverlay | null>(null);
  const layerRef = useRef<TerrierLayer | null>(null);
  const timeDataRef = useRef<{
    min: number;
    max: number;
    frameCount: number;
  } | null>(null);

  // Lifecycle refs
  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);
  // Set when the WASM runtime aborts (e.g. internal stack overflow).
  // The emscripten runtime cannot be re-initialized after an abort without a
  // page reload, so this permanently blocks re-init attempts.
  const hasFatalErrorRef = useRef(false);

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const playbackState = usePlaybackState();

  // Ref to access playback methods without adding to effect dependencies
  const playbackRef = useRef(playbackState);
  playbackRef.current = playbackState;

  const teardownLayer = useCallback((): void => {
    try {
      terrier.stop();
    } catch {
      // An aborted WASM module can throw during teardown too
    }
    removeRadarMaskLayer(map);

    overlayRef.current = null;
    layerRef.current = null;
    timeDataRef.current = null;
    isInitializingRef.current = false;
    setIsInitialized(false);
    playbackRef.current.setIsPlaying(false);
  }, [map]);

  // Initialize overlay and layer (only when visible)
  useEffect(() => {
    if (
      !map ||
      !visible ||
      isInitialized ||
      isInitializingRef.current ||
      hasFatalErrorRef.current
    ) {
      return () => {
        isMountedRef.current = false;
      };
    }

    isMountedRef.current = true;
    isInitializingRef.current = true;

    terrier.startMapLibre(
      import.meta.env.VITE_TERRIER_STACK_URL,
      import.meta.env.VITE_TERRIER_API_KEY,
      map,
      (overlay: TerrierOverlay) => {
        if (!isMountedRef.current) {
          isInitializingRef.current = false;
          return;
        }

        if (!overlay) {
          isInitializingRef.current = false;
          onError?.(new Error('Failed to initialize radar overlay'));
          return;
        }

        try {
          setupTerrierLayer({
            overlay,
            config,
            map,
            timeDataRef,
            overlayRef,
            layerRef,
            setIsInitialized,
            playbackState,
            playbackRef,
          });
        } catch (error) {
          isInitializingRef.current = false;
          onError?.(error as Error);
        }
      },
      belowLayer,
    );

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, visible, isInitialized]);

  // Cleanup on unmount
  useEffect(
    () => () => {
      const overlay = overlayRef.current;
      const layer = layerRef.current;

      if (overlay && layer) {
        try {
          overlay.timePause();
          overlay.stopLayer(layer);
        } catch {
          // Ignore cleanup errors
        }
      }

      removeRadarMaskLayer(map);

      overlayRef.current = null;
      layerRef.current = null;
      timeDataRef.current = null;
    },
    [map],
  );

  // Re-initialize when map style changes (e.g., base layer switch).
  useMapEvent(
    'style.load',
    () => {
      terrier.stop();
      // Mask layer is removed automatically with style change
      overlayRef.current = null;
      layerRef.current = null;
      timeDataRef.current = null;
      isInitializingRef.current = false;
      playbackRef.current.setFrames([]);
      playbackRef.current.jumpToFrame(0);
      playbackRef.current.setIsPlaying(false);
      setIsInitialized(false);
    },
    map,
    visible,
  );

  useRadarAnimation({
    overlay: isInitialized ? overlayRef.current : null,
    frames: playbackState.frames,
    isPlaying: playbackState.isPlaying,
    currentFrameIndex: playbackState.currentFrameIndex,
    animationPeriod: config.animationPeriod,
    onFrameAdvance: playbackState.jumpToFrame,
  });

  // Destroy overlay when visibility turns off to stop Terrier's internal RAF loop
  useEffect(() => {
    if (visible) return;

    // A deliberate toggle-off is the user's retry gesture — clear the fatal
    // latch so re-enabling radar gets a fresh init attempt.
    hasFatalErrorRef.current = false;

    if (!isInitialized) return;

    // this ensures _initMapLibre runs again on re-enable
    teardownLayer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, map]);

  useInterval(
    () => {
      try {
        layerRef.current?.refresh();
      } catch (error) {
        // thrown error during refresh means the WASM runtime likely aborted,
        // tear down the layer to prevent throwing every tick
        hasFatalErrorRef.current = true;
        teardownLayer();
        onError?.(error as Error);
      }
    },
    isInitialized && visible && layerRef.current
      ? config.refreshIntervalMs
      : null,
  );

  const onFrameChange = useCallback((index: number): void => {
    const overlay = overlayRef.current;
    const { frames: currentFrames } = playbackRef.current;
    if (!overlay || currentFrames.length === 0) return;

    const clamped = Math.max(0, Math.min(index, currentFrames.length - 1));
    const epochSec = currentFrames[clamped].timestamp.getTime() / 1000;
    overlay.setCurrentTime(epochSec);
    playbackRef.current.jumpToFrame(clamped, true);
  }, []);

  return {
    frames: playbackState.frames,
    currentFrameIndex: playbackState.currentFrameIndex,
    isPlaying: playbackState.isPlaying,
    isLoading: playbackState.isLoading,
    onFrameChange,
    onPlayPause: playbackState.togglePlayPause,
  };
};
